import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '../auth';
import { historyAPI, type HistoryItem } from '@/api/history';
import { handleApiError } from '@/lib/api';

export function History() {
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchHistory();
    } else {
      setLoading(false);
      setError('Необходимо войти в систему для просмотра истории');
    }
  }, [user]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await historyAPI.getSearchHistory();

      // Проверяем, является ли ответ массивом (прямой ответ) или объектом с полем ok
      if (Array.isArray(data)) {
        // Прямой массив данных
        setHistoryItems(data.slice(0, 100));
      } else if (data.ok && data.data) {
        // Объект с полем ok и data
        setHistoryItems(data.data.slice(0, 100));
      } else {
        throw new Error(data.error || 'Ошибка загрузки истории');
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const getSearchType = (query: string, isApi: boolean) => {
    if (isApi) return 'API запрос';
    
    // Определяем тип поиска по содержимому запроса
    if (/^\+?[78]\d{10}$/.test(query.replace(/\D/g, ''))) {
      return 'Поиск по телефону';
    }
    if (/\S+@\S+\.\S+/.test(query)) {
      return 'Поиск по email';
    }
    if (/^\d{10,12}$/.test(query)) {
      return 'Поиск по ИНН';
    }
    if (/\d{2}\.\d{2}\.\d{4}/.test(query)) {
      return 'Поиск с датой';
    }
    return 'Поиск по имени';
  };

  const getStatus = (resultsCount: number) => {
    return resultsCount > 0 ? 'Завершен' : 'Не найдено';
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-7xl">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">История поиска</h1>
            <p className="text-muted-foreground mt-2">
              Загрузка истории поиска...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-7xl">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">История поиска</h1>
            <p className="text-muted-foreground mt-2">
              Ошибка: {error}
            </p>
          </div>
          <Button onClick={fetchHistory}>Попробовать снова</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">История поиска</h1>
          <p className="text-muted-foreground mt-2">
            Просмотрите историю ваших запросов и их результаты
          </p>
        </div>

        <div className="text-sm text-muted-foreground">
          Ваши последние запросы: {historyItems.length} {historyItems.length >= 100 ? '(показаны последние 100)' : ''}
        </div>

      <div className="space-y-4">
        {historyItems.map((item) => {
          const searchType = getSearchType(item.query, item.is_api);
          const status = getStatus(item.results_count);
          
          return (
            <Card key={item.id}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{item.query}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        item.is_api 
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                      }`}>
                        {item.is_api ? 'API запрос' : searchType}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {item.created_at_str} • Результатов: {item.results_count}
                    </div>
                  </div>
                  <div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      status === 'Завершен' 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                    }`}>
                      {status}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {historyItems.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>История пуста</CardTitle>
            <CardDescription>
              У вас пока нет выполненных поисковых запросов
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/search">Выполнить первый поиск</Link>
            </Button>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
